import { useEffect, useState } from "react";
import { useFees, fmtUF } from "@/lib/useFees";
import { CLP } from "@/lib/format";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Receipt, GraduationCap, Hammer, Info, Mail, LifeBuoy, Coins, CreditCard, Gift, Clock } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ContactModal, CONTACT_REOPEN_KEY } from "@/components/ContactModal";
import { useAuth } from "@/context/AuthContext";

function PackRow({ p, onPick }) {
  return (
    <div className="relative rounded-xl border border-border p-3 flex flex-col" data-testid={`fees-pack-${p.id}`}>
      {p.saving_pct > 0 && <span className="absolute -top-2.5 right-2 rounded-full bg-coral px-2 py-0.5 text-[10px] font-bold text-white">{p.saving_pct}% ahorro</span>}
      <p className="font-bold">{p.name}</p>
      <p className="mt-1 text-xl font-extrabold flex items-center gap-1"><Coins className="w-4 h-4 text-amber-500" />{p.fichas}<span className="text-xs font-medium text-muted-foreground">ficha{p.fichas > 1 ? "s" : ""}</span></p>
      {p.fichas > p.paid && <p className="text-xs text-primary font-semibold">{p.paid} + {p.fichas - p.paid} gratis</p>}
      <p className="mt-1.5 text-sm"><strong>{fmtUF(p.price_uf)} UF</strong> + IVA</p>
      <p className="text-[11px] text-muted-foreground">Neto {CLP(p.price_clp)} + IVA {CLP(p.iva_clp)}</p>
      <p className="text-sm font-bold text-primary" data-testid={`fees-pack-total-${p.id}`}>Total {CLP(p.total_clp)}</p>
      <p className="text-[11px] text-muted-foreground flex-1">{fmtUF(p.unit_uf)} UF + IVA por ficha</p>
      <Button size="sm" className="mt-2.5 w-full" onClick={() => onPick(p.id)} data-testid={`fees-buy-${p.id}`}>Comprar</Button>
    </div>
  );
}

export function Footer() {
  const [open, setOpen] = useState(false);
  const fees = useFees();
  const [fichas, setFichas] = useState(null);
  useEffect(() => {
    if (open && !fichas) { api.get("/fichas/packs").then((r) => setFichas(r.data)).catch(() => {}); }
  }, [open, fichas]);
  const [contactOpen, setContactOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const pick = (id) => {
    const target = `/maestro?tab=estado&pack=${id}`;
    if (!user) { setOpen(false); navigate(`/login?next=${encodeURIComponent(target)}`); return; }
    if (user.role === "business") { setOpen(false); navigate(target); return; }
    toast.info(user.can_switch_role ? "Cambia a Modo Maestro desde tu menú para comprar fichas." : "Las fichas son para Maestros. Crea tu perfil de Maestro para comprarlas.");
  };

  useEffect(() => {
    if (user && sessionStorage.getItem(CONTACT_REOPEN_KEY)) { sessionStorage.removeItem(CONTACT_REOPEN_KEY); setContactOpen(true); }
  }, [user]);

  const year = new Date().getFullYear();

  return (
    <footer className="mt-20 border-t border-border bg-muted/30" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex flex-col items-center sm:items-start">
          <div className="flex items-center gap-2">
            <span className="font-display font-extrabold text-lg">VIVO<span className="text-primary">Taller</span></span>
            <span className="text-xs text-muted-foreground">© {year}</span>
          </div>
          <span className="text-[11px] text-muted-foreground/80" data-testid="footer-built-by">Construido por Nest App</span>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-3 text-sm font-medium">
          <Link to="/ayuda" data-testid="footer-help-link" className="inline-flex items-center gap-1.5 text-foreground hover:text-primary transition-colors">
            <LifeBuoy className="w-4 h-4" /> Centro de ayuda
          </Link>
          <span className="text-muted-foreground/50" aria-hidden="true">|</span>
          <button type="button" onClick={() => setContactOpen(true)} data-testid="footer-contact-link"
            className="inline-flex items-center gap-1.5 text-foreground hover:text-primary transition-colors">
            <Mail className="w-4 h-4" /> Contactar a VIVOTaller
          </button>
          <span className="text-muted-foreground/50" aria-hidden="true">|</span>
          <button
            type="button"
            onClick={() => setOpen(true)}
            data-testid="footer-fees-link"
            className="inline-flex items-center gap-1.5 text-foreground hover:text-primary transition-colors"
          >
            <Receipt className="w-4 h-4" /> Tarifas y Transparencia
          </button>
        </div>
      </div>
      <ContactModal open={contactOpen} onOpenChange={setContactOpen} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="fees-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Receipt className="w-5 h-5 text-primary" /> Tarifas y Transparencia
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <p className="text-muted-foreground">
              Crear tu cuenta en VIVOTaller es <strong>gratis</strong>, sin suscripciones ni cobros mensuales. Sólo se paga
              con <strong className="inline-flex items-center gap-0.5 align-middle"><Coins className="w-3.5 h-3.5 text-amber-500" data-testid="fees-fichas-icon" />Fichas</strong> que usa el Maestro para publicar sus talleres. Los precios están fijados en <strong>UF</strong>, se les suma el <strong>IVA (19%)</strong> y
              se convierten a pesos con el valor oficial del día (fuente: mindicador.cl){fees ? ` · 1 UF = ${CLP(Math.round(fees.uf_value))}` : ""}.
            </p>

            <div className="rounded-xl border border-border p-4 space-y-3" data-testid="fees-master">
              <div className="flex items-center gap-2 font-semibold">
                <Hammer className="w-4 h-4 text-primary" /> Maestro · Fichas
              </div>
              <p className="text-muted-foreground"><strong className="text-foreground">1 ficha = 1 publicación de taller.</strong> También puedes usar 1 ficha para destacar un taller {fichas?.feature_days ?? 3} días.</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1" data-testid="fees-packs">
                {fichas ? fichas.packs.map((p) => <PackRow key={p.id} p={p} onPick={pick} />) : <p className="text-xs text-muted-foreground">Cargando planes…</p>}
              </div>
              <ul className="space-y-1.5 text-xs text-muted-foreground">
                <li className="flex gap-2"><Gift className="w-3.5 h-3.5 shrink-0 mt-0.5 text-primary" />Fichas de regalo: 1 al completar tu perfil y 1 cada {fichas?.perf_step ?? 10} alumnos distintos que asistan entre todos tus talleres.</li>
                <li className="flex gap-2"><Clock className="w-3.5 h-3.5 shrink-0 mt-0.5 text-primary" />Las fichas vencen 1 año después de recibirlas y no son reembolsables ni canjeables por dinero.</li>
              </ul>
            </div>

            <div className="rounded-xl border border-border p-4 space-y-1.5" data-testid="fees-payment-method">
              <div className="flex items-center gap-2 font-semibold">
                <CreditCard className="w-4 h-4 text-primary" /> Método de pago
              </div>
              <p className="text-muted-foreground">El Maestro compra sus fichas en una plataforma de pago, con tarjeta de débito, crédito o prepago. El monto cobrado corresponde al tipo de opción seleccionada (Básico - Pack Creador - Pack Pro) con IVA incluido. Las fichas se cargan en la billetera del Maestro apenas se aprueba el pago.</p>
            </div>

            <div className="flex items-start gap-2 rounded-lg bg-primary/5 border border-primary/15 p-3 text-xs text-foreground" data-testid="fees-student-note">
              <GraduationCap className="w-4 h-4 shrink-0 mt-0.5 text-primary" />
              <span><strong>Para el Alumno:</strong> reservar tu cupo es gratis y se confirma al instante. El valor del taller se paga <strong>directamente al Maestro</strong>, sin cargos por servicio ni comisiones de VIVOTaller.</span>
            </div>

            <div className="flex items-start gap-2 rounded-lg bg-muted/50 p-3 text-xs text-muted-foreground">
              <Info className="w-4 h-4 shrink-0 mt-0.5 text-primary" />
              Si cambia el valor diario de la UF, los montos en CLP se recalculan automáticamente.
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </footer>
  );
}
